import { useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/contexts/AuthContext';
import type { Equipamento, MotivoExclusaoAtivo } from '../../../../models';
import { useEquipamentoMutations } from '../../../../hooks/data';
import { MOTIVO_EXCLUSAO_ATIVO_LABELS, bloqueioExclusaoAtivo, type ContextoAtivos } from './ativos.utils';

interface AtivoExclusaoSecaoProps {
  equipamento: Equipamento;
  contexto: ContextoAtivos;
  onExcluido: () => void;
}

/**
 * "Excluir ativo" (cadastrado por engano, duplicado...) — exclusão lógica: some das listas, mas
 * o registro fica e pode ser restaurado em "Excluídos". Só com o ativo livre (sem colaborador e
 * fora de Movimentação/Estadia em andamento).
 */
export default function AtivoExclusaoSecao({ equipamento, contexto, onExcluido }: AtivoExclusaoSecaoProps) {
  const { user } = useAuth();
  const { excluirAtivo } = useEquipamentoMutations();
  const [aberto, setAberto] = useState(false);
  const [motivoTipo, setMotivoTipo] = useState<MotivoExclusaoAtivo | ''>('');
  const [motivo, setMotivo] = useState('');

  const bloqueio = bloqueioExclusaoAtivo(equipamento, contexto);
  const faltaMotivo = !motivoTipo || (motivoTipo === 'outro' && !motivo.trim());

  // mutateAsync, não mutate + onSuccess: a lista em tempo real tira o ativo assim que a
  // exclusão grava e este componente desmonta — o callback do mutate não chegaria a rodar e a
  // ficha ficaria aberta num ativo que sumiu ("Novo ativo / Carregando...").
  const confirmar = async () => {
    if (!user || !motivoTipo || faltaMotivo) return;
    try {
      await excluirAtivo.mutateAsync({
        id: equipamento.id,
        motivoTipo,
        motivo: motivo.trim() || null,
        ator: { id: user.collaboratorId ?? user.uid, name: user.displayName ?? user.email ?? '', avatar: null },
      });
      onExcluido();
    } catch {
      /* toast no onError da mutation */
    }
  };

  if (!aberto) {
    return (
      <div className="flex justify-end">
        <button
          type="button"
          className="flex items-center gap-1.5 text-sm text-red-600 hover:underline"
          onClick={() => setAberto(true)}
        >
          <TrashIcon className="h-4 w-4" />
          Excluir ativo
        </button>
      </div>
    );
  }

  return (
    <section className="space-y-3 rounded-md border border-red-200 bg-red-50 p-4">
      <p className="text-sm font-semibold text-red-800">Excluir ativo</p>
      {bloqueio ? (
        <p className="text-sm text-red-700">{bloqueio}</p>
      ) : (
        <>
          <p className="text-xs text-red-700">
            Some das listas, contagens e buscas, mas o registro fica (com o histórico) e pode ser restaurado em
            "Excluídos". A importação do GLPI não recria um ativo excluído.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Motivo *</label>
              <select
                className="input-primary mt-1 w-full"
                value={motivoTipo}
                onChange={(e) => setMotivoTipo(e.target.value as MotivoExclusaoAtivo | '')}
              >
                <option value="">Selecione</option>
                {(Object.keys(MOTIVO_EXCLUSAO_ATIVO_LABELS) as MotivoExclusaoAtivo[]).map((valor) => (
                  <option key={valor} value={valor}>
                    {MOTIVO_EXCLUSAO_ATIVO_LABELS[valor]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Detalhe {motivoTipo === 'outro' ? '*' : '(opcional)'}
              </label>
              <input
                type="text"
                className="input-primary mt-1 w-full"
                placeholder={motivoTipo === 'duplicado' ? 'Ex.: o certo é o RLMP071' : ''}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
              />
            </div>
          </div>
        </>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary text-sm" onClick={() => setAberto(false)}>
          {bloqueio ? 'Fechar' : 'Cancelar'}
        </button>
        {!bloqueio && (
          <button
            type="button"
            className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            disabled={faltaMotivo || excluirAtivo.isPending}
            onClick={confirmar}
          >
            {excluirAtivo.isPending ? 'Excluindo...' : 'Excluir'}
          </button>
        )}
      </div>
    </section>
  );
}
